/**
 * IntelliHealth Clinical Actions
 * 
 * Types and extraction logic for parsing AI recommendations into actionable clinical items.
 * These actions are displayed as clickable cards that trigger clinical workflows.
 */

// ============================================================================
// TYPES
// ============================================================================

export type ActionType = 'prescribe' | 'protocol' | 'labs' | 'followup' | 'refer' | 'monitor' | 'lifestyle' | 'other';
export type ActionPriority = 'high' | 'medium' | 'low';

export interface MedicationInfo {
  name: string;
  dose?: string;
  frequency?: string;
  duration?: string;
  route?: string;
  /** Pre-built sig (instructions) from structured recommendations */
  sig?: string;
  /** Quantity from structured recommendations */
  quantity?: number;
  /** Refills from structured recommendations */
  refills?: number;
}

export interface LabTestInfo {
  name: string;
  code?: string;
  reason?: string;
  loincCode?: string;
}

export interface ClinicalAction {
  id: string;
  type: ActionType;
  title: string;
  description: string;
  priority: ActionPriority;
  sourceText: string; // Original text from AI recommendation
  metadata: {
    medications?: MedicationInfo[];
    labTests?: LabTestInfo[];
    followupDays?: number;
    followupType?: string;
    specialtyReferral?: string;
    protocolName?: string;
    monitoringItems?: string[];
    lifestyleRecommendations?: string[];
  };
}

export interface ExtractedActions {
  actions: ClinicalAction[];
  hasHighPriorityActions: boolean;
  summary: string;
}

// ============================================================================
// COMMON MEDICATION PATTERNS
// ============================================================================

const COMMON_MEDICATIONS = [
  // Hormone Therapy
  'estradiol', 'estrogen', 'progesterone', 'testosterone', 'dhea', 'pregnenolone',
  'levothyroxine', 'synthroid', 'armour thyroid', 'cytomel', 'liothyronine',
  // Metabolic
  'metformin', 'semaglutide', 'ozempic', 'wegovy', 'tirzepatide', 'mounjaro',
  'liraglutide', 'victoza', 'saxenda',
  // Supplements
  'vitamin d', 'vitamin b12', 'iron', 'magnesium', 'omega-3', 'fish oil',
  'probiotics', 'melatonin', 'ashwagandha', 'maca',
  // Other common
  'spironolactone', 'finasteride', 'minoxidil', 'metoprolol', 'lisinopril',
  'atorvastatin', 'rosuvastatin', 'amlodipine', 'omeprazole', 'sertraline',
  'escitalopram', 'bupropion', 'trazodone', 'gabapentin', 'pregabalin'
];

const MEDICATION_PATTERNS = [
  /(?:prescribe|recommend|start|initiate|consider)\s+([a-zA-Z0-9\-]+(?:\s+\d+\s*(?:mg|mcg|iu|ml))?)/gi,
  /([a-zA-Z0-9\-]+)\s+(\d+\s*(?:mg|mcg|iu|ml))\s+(?:daily|twice daily|bid|tid|qd|once daily|weekly)/gi,
  /treatment\s+with\s+([a-zA-Z0-9\-]+)/gi,
];

// ============================================================================
// LAB TEST PATTERNS
// ============================================================================

const COMMON_LAB_TESTS = [
  // Hormones
  'estradiol', 'progesterone', 'testosterone', 'dhea-s', 'dhea', 'cortisol',
  'fsh', 'lh', 'prolactin', 'shbg', 'free testosterone', 'total testosterone',
  // Thyroid
  'tsh', 't3', 't4', 'free t3', 'free t4', 'thyroid antibodies', 'tpo', 'anti-tpo',
  'thyroglobulin', 'reverse t3',
  // Metabolic
  'fasting glucose', 'hba1c', 'a1c', 'fasting insulin', 'homa-ir', 'lipid panel',
  'cholesterol', 'hdl', 'ldl', 'triglycerides', 'apo b', 'lp(a)',
  // General
  'cbc', 'cmp', 'complete blood count', 'metabolic panel', 'liver enzymes',
  'kidney function', 'creatinine', 'bun', 'ferritin', 'iron panel', 'vitamin d',
  'vitamin b12', 'folate', 'homocysteine', 'crp', 'hs-crp', 'sed rate', 'esr',
  'amh', 'anti-mullerian hormone'
];

const LAB_PATTERNS = [
  /(?:order|check|measure|test|monitor|evaluate)\s+([a-zA-Z0-9\-\s]+(?:levels?)?)/gi,
  /([a-zA-Z0-9\-\s]+)\s+(?:levels?|panel|test)\s+(?:should be|need to be|to be)\s+(?:checked|monitored|evaluated)/gi,
  /repeat\s+([a-zA-Z0-9\-\s]+)\s+(?:in|after)/gi,
];

// ============================================================================
// SPECIALTY PATTERNS
// ============================================================================

const SPECIALTIES = [
  'endocrinology', 'endocrinologist',
  'cardiology', 'cardiologist',
  'gynecology', 'gynecologist', 'ob/gyn', 'obgyn',
  'psychiatry', 'psychiatrist',
  'dermatology', 'dermatologist',
  'neurology', 'neurologist',
  'rheumatology', 'rheumatologist',
  'urology', 'urologist',
  'nephrology', 'nephrologist',
  'gastroenterology', 'gastroenterologist',
  'oncology', 'oncologist',
  'nutrition', 'nutritionist', 'dietitian',
  'physical therapy', 'physical therapist',
  'mental health', 'therapist', 'counselor', 'psychologist'
];

// ============================================================================
// EXTRACTION FUNCTIONS
// ============================================================================

/**
 * Extract medications from text
 */
function extractMedications(text: string): MedicationInfo[] {
  const medications: MedicationInfo[] = [];
  const lowerText = text.toLowerCase();
  
  // Check for common medication names
  for (const med of COMMON_MEDICATIONS) {
    if (lowerText.includes(med.toLowerCase())) {
      // Try to extract dose
      const dosePattern = new RegExp(`${med}\\s*(\\d+\\s*(?:mg|mcg|iu|ml|units?))?`, 'gi');
      const match = dosePattern.exec(text);
      
      medications.push({
        name: med.charAt(0).toUpperCase() + med.slice(1),
        dose: match?.[1]?.trim() || undefined
      });
    }
  }
  
  // Also check patterns
  for (const pattern of MEDICATION_PATTERNS) {
    const regex = new RegExp(pattern);
    let match;
    while ((match = regex.exec(text)) !== null) {
      const name = match[1]?.trim();
      if (name && name.length > 2 && !medications.find(m => m.name.toLowerCase() === name.toLowerCase())) {
        medications.push({ name });
      }
    }
  }
  
  return medications;
}

/**
 * Extract lab tests from text
 */
function extractLabTests(text: string): LabTestInfo[] {
  const labs: LabTestInfo[] = [];
  const lowerText = text.toLowerCase();
  
  for (const lab of COMMON_LAB_TESTS) {
    if (lowerText.includes(lab.toLowerCase())) {
      if (!labs.find(l => l.name.toLowerCase() === lab.toLowerCase())) {
        labs.push({
          name: lab.toUpperCase() === lab ? lab : lab.charAt(0).toUpperCase() + lab.slice(1)
        });
      }
    }
  }
  
  return labs;
}

/**
 * Extract follow-up timing from text
 */
function extractFollowup(text: string): { days: number; type: string } | null {
  const patterns = [
    /follow[- ]?up\s+(?:in|after)\s+(\d+)\s*(day|week|month)s?/gi,
    /(?:recheck|revisit|return)\s+(?:in|after)\s+(\d+)\s*(day|week|month)s?/gi,
    /(\d+)\s*(day|week|month)s?\s+follow[- ]?up/gi,
    /schedule\s+(?:a\s+)?(?:follow[- ]?up|appointment)\s+(?:in|for)\s+(\d+)\s*(day|week|month)s?/gi,
  ];
  
  for (const pattern of patterns) {
    const match = pattern.exec(text);
    if (match) {
      const num = parseInt(match[1]);
      const unit = match[2].toLowerCase();
      
      let days = num;
      if (unit.includes('week')) days = num * 7;
      if (unit.includes('month')) days = num * 30;
      
      return { days, type: `${num} ${unit}${num > 1 ? 's' : ''}` };
    }
  }
  
  return null;
}

/**
 * Extract specialty referral from text
 */
function extractReferral(text: string): string | null {
  const lowerText = text.toLowerCase();
  
  for (const specialty of SPECIALTIES) {
    if (lowerText.includes(specialty)) {
      // Check if it's in context of referral
      const referralPatterns = [
        new RegExp(`refer\\s+(?:to\\s+)?(?:an?\\s+)?${specialty}`, 'i'),
        new RegExp(`${specialty}\\s+(?:referral|consult|consultation)`, 'i'),
        new RegExp(`consult(?:ation)?\\s+with\\s+(?:an?\\s+)?${specialty}`, 'i'),
      ];
      
      for (const pattern of referralPatterns) {
        if (pattern.test(text)) {
          return specialty.charAt(0).toUpperCase() + specialty.slice(1);
        }
      }
    }
  }
  
  return null;
}

/**
 * Determine action priority based on keywords
 */
function determinePriority(text: string): ActionPriority {
  const lowerText = text.toLowerCase();
  
  const highPriorityKeywords = [
    'urgent', 'immediately', 'critical', 'severe', 'significant',
    'strongly recommend', 'essential', 'priority', 'important'
  ];
  
  const mediumPriorityKeywords = [
    'recommend', 'should', 'consider', 'suggest', 'advise'
  ];
  
  for (const keyword of highPriorityKeywords) {
    if (lowerText.includes(keyword)) return 'high';
  }
  
  for (const keyword of mediumPriorityKeywords) {
    if (lowerText.includes(keyword)) return 'medium';
  }
  
  return 'low';
}

/**
 * Generate unique ID
 */
function generateId(): string {
  return `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// MAIN EXTRACTION FUNCTION
// ============================================================================

/**
 * Extract clinical actions from AI recommendation text
 */
export function extractClinicalActions(markdown: string): ExtractedActions {
  if (!markdown || typeof markdown !== 'string') {
    return { actions: [], hasHighPriorityActions: false, summary: 'No recommendations available' };
  }

  const actions: ClinicalAction[] = [];
  const lines = markdown.split('\n');
  
  let currentSection = '';
  let currentContent: string[] = [];
  
  // Process sections
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headingMatch = line.match(/^#{1,4}\s+(.+)$/);
    
    if (headingMatch) {
      // Process previous section
      if (currentSection && currentContent.length > 0) {
        const sectionText = currentContent.join('\n');
        const sectionActions = extractActionsFromSection(currentSection, sectionText);
        actions.push(...sectionActions);
      }
      
      currentSection = headingMatch[1].trim();
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  }
  
  // Process last section
  if (currentSection && currentContent.length > 0) {
    const sectionText = currentContent.join('\n');
    const sectionActions = extractActionsFromSection(currentSection, sectionText);
    actions.push(...sectionActions);
  }
  
  // If no structured sections found, try to extract from entire text
  if (actions.length === 0) {
    const fullTextActions = extractActionsFromSection('Recommendations', markdown);
    actions.push(...fullTextActions);
  }
  
  // Deduplicate actions
  const uniqueActions = deduplicateActions(actions);
  
  // Sort by priority
  uniqueActions.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
  
  const hasHighPriorityActions = uniqueActions.some(a => a.priority === 'high');
  const summary = generateActionsSummary(uniqueActions);
  
  return {
    actions: uniqueActions,
    hasHighPriorityActions,
    summary
  };
}

/**
 * Extract actions from a specific section
 */
function extractActionsFromSection(sectionTitle: string, sectionText: string): ClinicalAction[] {
  const actions: ClinicalAction[] = [];
  const lowerTitle = sectionTitle.toLowerCase();
  
  // Extract medications
  const medications = extractMedications(sectionText);
  if (medications.length > 0) {
    actions.push({
      id: generateId(),
      type: 'prescribe',
      title: 'Prescribe Medications',
      description: `${medications.length} medication${medications.length > 1 ? 's' : ''} recommended: ${medications.map(m => m.name).join(', ')}`,
      priority: determinePriority(sectionText),
      sourceText: sectionText.substring(0, 200),
      metadata: { medications }
    });
  }
  
  // Extract lab tests
  const labTests = extractLabTests(sectionText);
  if (labTests.length > 0) {
    actions.push({
      id: generateId(),
      type: 'labs',
      title: 'Order Lab Tests',
      description: `${labTests.length} lab test${labTests.length > 1 ? 's' : ''} recommended`,
      priority: determinePriority(sectionText),
      sourceText: sectionText.substring(0, 200),
      metadata: { labTests }
    });
  }
  
  // Extract follow-up
  const followup = extractFollowup(sectionText);
  if (followup) {
    actions.push({
      id: generateId(),
      type: 'followup',
      title: 'Schedule Follow-up',
      description: `Follow-up recommended in ${followup.type}`,
      priority: 'medium',
      sourceText: sectionText.substring(0, 200),
      metadata: { followupDays: followup.days, followupType: followup.type }
    });
  }
  
  // Extract referral
  const referral = extractReferral(sectionText);
  if (referral) {
    actions.push({
      id: generateId(),
      type: 'refer',
      title: 'Specialist Referral',
      description: `Referral to ${referral} recommended`,
      priority: determinePriority(sectionText),
      sourceText: sectionText.substring(0, 200),
      metadata: { specialtyReferral: referral }
    });
  }
  
  // Check for treatment protocol mentions
  if (lowerTitle.includes('protocol') || lowerTitle.includes('treatment plan') ||
      sectionText.toLowerCase().includes('treatment protocol') ||
      sectionText.toLowerCase().includes('therapy protocol')) {
    actions.push({
      id: generateId(),
      type: 'protocol',
      title: 'Treatment Protocol',
      description: 'Structured treatment protocol available',
      priority: 'medium',
      sourceText: sectionText.substring(0, 200),
      metadata: { protocolName: sectionTitle }
    });
  }
  
  // Check for monitoring recommendations
  if (lowerTitle.includes('monitor') || sectionText.toLowerCase().includes('monitor')) {
    const monitoringItems: string[] = [];
    const monitorPatterns = [
      /monitor\s+([a-zA-Z0-9\-\s,]+?)(?:\.|,|;|$)/gi,
      /track\s+([a-zA-Z0-9\-\s,]+?)(?:\.|,|;|$)/gi,
    ];
    
    for (const pattern of monitorPatterns) {
      let match;
      while ((match = pattern.exec(sectionText)) !== null) {
        if (match[1]?.trim()) {
          monitoringItems.push(match[1].trim());
        }
      }
    }
    
    if (monitoringItems.length > 0) {
      actions.push({
        id: generateId(),
        type: 'monitor',
        title: 'Monitoring Plan',
        description: `Monitor: ${monitoringItems.slice(0, 3).join(', ')}${monitoringItems.length > 3 ? '...' : ''}`,
        priority: 'low',
        sourceText: sectionText.substring(0, 200),
        metadata: { monitoringItems }
      });
    }
  }
  
  return actions;
}

/**
 * Remove duplicate actions
 */
function deduplicateActions(actions: ClinicalAction[]): ClinicalAction[] {
  const seen = new Map<string, ClinicalAction>();
  
  for (const action of actions) {
    const key = `${action.type}-${action.title}`;
    const existing = seen.get(key);
    
    if (!existing) {
      seen.set(key, action);
    } else {
      // Merge metadata
      if (action.metadata.medications) {
        existing.metadata.medications = [
          ...(existing.metadata.medications || []),
          ...action.metadata.medications
        ];
      }
      if (action.metadata.labTests) {
        existing.metadata.labTests = [
          ...(existing.metadata.labTests || []),
          ...action.metadata.labTests
        ];
      }
      // Keep higher priority
      if (action.priority === 'high' && existing.priority !== 'high') {
        existing.priority = 'high';
      }
    }
  }
  
  return Array.from(seen.values());
}

/**
 * Generate a summary of actions
 */
function generateActionsSummary(actions: ClinicalAction[]): string {
  if (actions.length === 0) return 'No specific actions identified';
  
  const counts: Record<ActionType, number> = {
    prescribe: 0,
    protocol: 0,
    labs: 0,
    followup: 0,
    refer: 0,
    monitor: 0,
    lifestyle: 0,
    other: 0
  };
  
  for (const action of actions) {
    counts[action.type]++;
  }
  
  const parts: string[] = [];
  if (counts.prescribe > 0) parts.push(`${counts.prescribe} prescription${counts.prescribe > 1 ? 's' : ''}`);
  if (counts.labs > 0) parts.push(`${counts.labs} lab order${counts.labs > 1 ? 's' : ''}`);
  if (counts.followup > 0) parts.push('follow-up');
  if (counts.refer > 0) parts.push(`${counts.refer} referral${counts.refer > 1 ? 's' : ''}`);
  
  return parts.length > 0 ? parts.join(', ') : 'Review recommendations';
}

// ============================================================================
// ACTION ICONS AND LABELS
// ============================================================================

export const ACTION_CONFIG: Record<ActionType, { label: string; buttonText: string; color: string }> = {
  prescribe: { label: 'Prescription', buttonText: 'Create Prescription', color: 'emerald' },
  protocol: { label: 'Treatment Protocol', buttonText: 'Select Protocol', color: 'blue' },
  labs: { label: 'Lab Tests', buttonText: 'Order Labs', color: 'purple' },
  followup: { label: 'Follow-up', buttonText: 'Schedule Appointment', color: 'amber' },
  refer: { label: 'Referral', buttonText: 'Create Referral', color: 'cyan' },
  monitor: { label: 'Monitoring', buttonText: 'Set Up Monitoring', color: 'slate' },
  lifestyle: { label: 'Lifestyle', buttonText: 'View Recommendations', color: 'green' },
  other: { label: 'Action', buttonText: 'Take Action', color: 'gray' }
};

export default {
  extractClinicalActions,
  ACTION_CONFIG
};
